import React, { useMemo, useState } from 'react';
import PDIcn from './pdIcons';
import { ext, fileClass, langCode } from '../AllProjects/projectHelpers';

const FILE_FILTERS = {
    All: () => true,
    Documents: (f) => ['DOC', 'DOCX', 'PDF', 'TXT', 'HTM', 'HTML'].includes(f.lbl),
    Strings: (f) => ['JSO', 'JSON', 'YML', 'YAML', 'MD'].includes(f.lbl),
    Spreadsheets: (f) => ['XLS', 'CSV', 'XLI', 'XLF'].includes(f.lbl),
};

const FILE_STATUS = {
    synced: { label: 'Synced', cls: 'ok' },
    changed: { label: 'Update available', cls: 'warn' },
    importing: { label: 'Importing…', cls: 'busy' },
};

const EXPORT_STATE = {
    ready: { label: 'Ready', cls: 'ok' },
    partial: { label: 'Partial', cls: 'warn' },
    progress: { label: 'In progress', cls: 'busy' },
    queued: { label: 'Queued', cls: 'quiet' },
};

const fmtBytes = (n) => {
    if (!n) return '—';
    if (n > 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${n} B`;
};

const fmtRel = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    const ms = Date.now() - d.getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return d.toLocaleDateString();
};

const sourceForProject = (p) => {
    if (p.creationSource === 'slack') return { name: 'Slack', cls: 'sl', letter: 'S' };
    if (p.creationSource === 'figma') return { name: 'Figma', cls: 'fg', letter: 'F' };
    if (p.creationSource === 'api') return { name: 'API', cls: 'gh', letter: 'A' };
    if (p.fileUrl && p.fileUrl.includes('drive.google')) return { name: 'Google Drive', cls: 'gd', letter: 'G' };
    if (p.fileUrl && p.fileUrl.includes('dropbox')) return { name: 'Dropbox', cls: 'db', letter: 'D' };
    return { name: 'Upload', cls: '', letter: 'U' };
};

const computeFiles = (project) => {
    const list = [];
    if (project.fileUrl || project.fileName) {
        const name = project.fileName || (project.fileUrl || '').split('/').pop() || project.name;
        const e = ext({ fileName: name });
        list.push({
            id: project._id,
            name,
            path: project.category || 'Project',
            lbl: e,
            ic: fileClass(e),
            words: project.wordCount || 0,
            segs: (project.sections || []).length,
            src: sourceForProject(project),
            status: 'synced',
            ver: project.currentVersion ? `v${project.currentVersion + 1}` : 'v1',
            updated: project.updatedAt || project.createdAt,
        });
    }
    (project.documentVersions || []).slice(0, -1).forEach((v, i) => {
        list.push({
            id: `${project._id}-v${i}`,
            name: `${project.name} (v${i + 1})`,
            path: 'History',
            lbl: ext({ fileName: project.fileName || '' }),
            ic: '',
            words: 0,
            segs: 0,
            src: { name: 'Version', cls: '', letter: 'V' },
            status: 'synced',
            ver: `v${i + 1}`,
            updated: v.timestamp,
            historical: true,
        });
    });
    return list;
};

const computeExports = (project, languages) => {
    return languages.map((l) => {
        const code = langCode(l.code || l);
        const pct = Math.round(l.progress || 0);
        const state = pct === 100 ? 'ready' : pct >= 60 ? 'partial' : pct > 0 ? 'progress' : 'queued';
        return {
            code,
            name: l.name || code,
            pct,
            size: pct >= 60 ? fmtBytes((project.wordCount || 0) * 6) : '—',
            state,
            projectId: l.projectId,
        };
    });
};

const FilesTab = ({ project, languages, onReimport, onExport }) => {
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('All');

    const sourceFiles = useMemo(() => computeFiles(project), [project]);
    const filtered = sourceFiles
        .filter(FILE_FILTERS[filter])
        .filter((f) => f.name.toLowerCase().includes(q.toLowerCase()) || (f.path || '').toLowerCase().includes(q.toLowerCase()));
    const totalWords = sourceFiles.reduce((a, f) => a + f.words, 0);
    const totalSegs = sourceFiles.reduce((a, f) => a + f.segs, 0);
    const exports = useMemo(() => computeExports(project, languages), [project, languages]);

    return (
        <div className="rs-pd-files">
            <div className="pdf-sync">
                <span className={`ic ${sourceForProject(project).cls}`}>{sourceForProject(project).letter}</span>
                <div className="body">
                    <b>Source: {sourceForProject(project).name}</b>
                    <span>Last update {fmtRel(project.updatedAt || project.createdAt)}</span>
                </div>
                <button type="button" className="btn-q sm">{PDIcn.arrow}<span>Sync now</span></button>
            </div>

            <div className="rs-card pdf-card">
                <div className="pdf-h">
                    <h3>Source files</h3>
                    <span className="pdf-count">{sourceFiles.length}</span>
                    <span className="pdf-sub">{totalWords.toLocaleString()} words · {totalSegs} segments</span>
                    <div className="grow" />
                    <div className="pdf-search">
                        {PDIcn.arrow}
                        <input
                            placeholder="Search files…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <button type="button" className="btn-q sm" onClick={() => onReimport && onReimport(project._id)}>
                        {PDIcn.arrow}<span>Re-import</span>
                    </button>
                    <button type="button" className="btn-acc sm" onClick={() => onExport && onExport(project._id)}>
                        {PDIcn.download}<span>Export all</span>
                    </button>
                </div>

                <div className="pdf-chips">
                    {Object.keys(FILE_FILTERS).map((k) => (
                        <button
                            key={k}
                            type="button"
                            className={`pdf-chip ${filter === k ? 'on' : ''}`}
                            onClick={() => setFilter(k)}
                        >
                            {k}
                        </button>
                    ))}
                </div>

                <div className="pdf-table">
                    <div className="pdf-row head">
                        <span>File</span>
                        <span className="num">Words</span>
                        <span className="num">Segments</span>
                        <span>Source</span>
                        <span>Status</span>
                        <span />
                    </div>
                    {filtered.length === 0 && (
                        <div className="pdf-empty">No files match &ldquo;{q}&rdquo;.</div>
                    )}
                    {filtered.map((f) => {
                        const st = FILE_STATUS[f.status] || FILE_STATUS.synced;
                        return (
                            <div className="pdf-row" key={f.id}>
                                <div className="pdf-file">
                                    <span className={`fic ${f.ic}`}>{f.lbl}</span>
                                    <span className="nm-wrap">
                                        <b>{f.name} <span className="ver">{f.ver}</span></b>
                                        <span>{f.path} · updated {fmtRel(f.updated)}</span>
                                    </span>
                                </div>
                                <span className="num">{f.words.toLocaleString()}</span>
                                <span className="num">{f.segs}</span>
                                <span className="pdf-src">
                                    <span className={`src-ic ${f.src.cls}`}>{f.src.letter}</span>
                                    {f.src.name}
                                </span>
                                <span>
                                    <span className={`pdf-status ${st.cls}`}>{st.label}</span>
                                    {f.status === 'changed' && (
                                        <button type="button" className="pdf-reimport" onClick={() => onReimport && onReimport(f.id)}>
                                            Re-import
                                        </button>
                                    )}
                                </span>
                                <span className="pdf-more">{PDIcn.more}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="rs-card pdf-card">
                <div className="pdf-h">
                    <h3>Deliverables</h3>
                    <span className="pdf-count">{exports.length}</span>
                    <span className="pdf-sub">Translated output per language</span>
                    <div className="grow" />
                </div>
                <div className="pdf-exports">
                    {exports.length === 0 && (
                        <div className="pdf-empty">No target languages yet.</div>
                    )}
                    {exports.map((e) => {
                        const st = EXPORT_STATE[e.state] || EXPORT_STATE.queued;
                        const dl = e.state === 'ready' || e.state === 'partial';
                        return (
                            <div className="pdf-export" key={e.code}>
                                <span className="ex-lang">
                                    <span className="code">{e.code}</span>{e.name}
                                </span>
                                <span className="ex-prog">
                                    <span className="bar"><span style={{ width: `${e.pct}%` }} /></span>
                                    <span className="pct">{e.pct}%</span>
                                </span>
                                <span className="ex-size">{e.size}</span>
                                <span className={`pdf-status ${st.cls}`}>{st.label}</span>
                                <button
                                    type="button"
                                    className="btn-q sm"
                                    disabled={!dl}
                                    style={!dl ? { opacity: 0.45, cursor: 'not-allowed' } : null}
                                    onClick={() => onExport && onExport(e.projectId || project._id)}
                                >
                                    {PDIcn.download}<span>{e.state === 'partial' ? 'Partial' : 'Download'}</span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default FilesTab;
